/**
 * Client for managing user sender preferences
 */

import { getStoredSessionId } from './auth-client';

const CF_WORKER_URL = import.meta.env.VITE_CLOUDFLARE_WORKER_URL || '';

export interface SenderPreference {
  id: string;
  user_id: string;
  sender_pattern: string;
  sender_domain: string | null;
  preferred_category: 'do_nothing' | 'delete' | 'archive' | 'unsubscribe';
  source: 'manual_move' | 'bulk_action' | 'sender_modal' | 'settings_page';
  times_applied: number;
  created_at: number;
  updated_at: number;
}

export interface UpsertPreferenceInput {
  senderPattern: string;
  preferredCategory: 'do_nothing' | 'delete' | 'archive' | 'unsubscribe';
  source: 'manual_move' | 'bulk_action' | 'sender_modal' | 'settings_page';
}

export class PreferencesClient {
  /**
   * Get all sender preferences for the current user
   */
  async getPreferences(): Promise<SenderPreference[]> {
    const sessionId = getStoredSessionId();

    const response = await fetch(`${CF_WORKER_URL}/api/preferences`, {
      method: 'GET',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...(sessionId ? { 'X-Session-Id': sessionId } : {}),
      },
    });

    const result = await response.json();

    if (!response.ok || !result.success) {
      throw new Error(result.error?.message || 'Failed to get preferences');
    }

    return result.data;
  }

  /**
   * Create or update a sender preference
   */
  async upsertPreference(input: UpsertPreferenceInput): Promise<SenderPreference> {
    const sessionId = getStoredSessionId();

    const response = await fetch(`${CF_WORKER_URL}/api/preferences`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...(sessionId ? { 'X-Session-Id': sessionId } : {}),
      },
      body: JSON.stringify(input),
    });

    const result = await response.json();

    if (!response.ok || !result.success) {
      throw new Error(result.error?.message || 'Failed to save preference');
    }

    return result.data;
  }

  /**
   * Bulk upsert preferences (for syncing from localStorage)
   */
  async bulkUpsertPreferences(preferences: UpsertPreferenceInput[]): Promise<{
    saved: SenderPreference[];
    errors: Array<{ senderPattern: string; error: string }>;
    summary: { successCount: number; errorCount: number };
  }> {
    const sessionId = getStoredSessionId();

    const response = await fetch(`${CF_WORKER_URL}/api/preferences/bulk`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...(sessionId ? { 'X-Session-Id': sessionId } : {}),
      },
      body: JSON.stringify({ preferences }),
    });

    const result = await response.json();

    if (!response.ok || !result.success) {
      throw new Error(result.error?.message || 'Failed to save preferences');
    }

    return result.data;
  }

  /**
   * Delete a sender preference
   */
  async deletePreference(preferenceId: string): Promise<void> {
    const sessionId = getStoredSessionId();

    const response = await fetch(`${CF_WORKER_URL}/api/preferences/${preferenceId}`, {
      method: 'DELETE',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...(sessionId ? { 'X-Session-Id': sessionId } : {}),
      },
    });

    const result = await response.json();

    if (!response.ok || !result.success) {
      throw new Error(result.error?.message || 'Failed to delete preference');
    }
  }
}

// Singleton instance
export const preferencesClient = new PreferencesClient();
